<template>
  <div class="row">
    <div class="col-12" v-if="!order">
      <h3 class="mb-5"> Data Not Found </h3>
    </div>
    <div class="col-12" v-if="order">
      <h3 class="mb-5"> Order #{{ order.order_id }} </h3>
      <div class="row">
        <div class="col-12 col-md-12 col-lg-12">
          <div class="mb-5 mt-5" style="display: flex; flex-wrap: nowrap">
            <StateItem
              :icon="'file-add'"
              :description="$t('purchase.state.created')"
              :completed="true"
              :subDescription="$moment(created ? created.update_time : order.order_date).format('YYYY-MM-DD HH:mm')"
            />
            <StateItem
              :icon="'credit-card'"
              :description="!!paid ? $t('purchase.state.paid') : $t('purchase.state.waitingPayment')"
              :active="true"
              :completed="!!paid"
              :subDescription="
                !!paid && !order.order_state === 'canceled' && isExpired ? $moment(paid.update_time).format('YYYY-MM-DD HH:mm') : ''
              "
              v-if="order.order_state !== 'canceled' && !isExpired"
            />
            <StateItem
              :icon="'hourglass'"
              :description="$t('purchase.state.processing')"
              :active="!!paid"
              :completed="!!shippingState"
              v-if="order.order_state !== 'canceled' && !isExpired"
            />
            <StateItem
              :icon="'rocket'"
              :description="!!shippingState ? $t('purchase.state.shipped') : $t('purchase.state.shipping')"
              :active="!!shippingState && !deliveredState"
              :completed="!!deliveredState"
              :subDescription="
                !!shippingState ?
                  `${$moment(shippingState.update_time).format('YYYY-MM-DD HH:mm')} -- ${('AWB: ' + shippingInfo.logistic.tracking_no || 'No AWB provided yet')}` : ''
              "
              v-if="order.order_state !== 'canceled' && !isExpired"
            />
            <StateItem
              :icon="'home'"
              :description="!!deliveredState ? $t('purchase.state.finish') : $t('purchase.state.finish')"
              :completed="!!deliveredState"
              :subDescription="
                !!deliveredState ? $moment(deliveredState.update_time).format('YYYY-MM-DD HH:mm') : ''
              "
              v-if="order.order_state !== 'canceled' && !isExpired"
            />
            <StateItem
              :icon="'close'"
              :description="'Dibatalkan'"
              :danger="true"
              :subDescription="!!isExpired ? 'Pembayaran Melebihi Batas Waktu' : ''"
              v-if="order.order_state === 'canceled' || isExpired"
            />
          </div>
        </div>
      </div>
      <Countdown class="mb-3" :expire="expiredDate" v-if="!paid" />
    </div>
    <div class="col-12" v-if="order">
      <div class="d-flex justify-content-center flex-wrap">
        <section class="card mb-5 col-12 col-md-10" style="padding: 2rem">
          <h5 class="mb-3"> {{ $t('purchase.payment.information') }} </h5>
          <div class="row mb-2">
            <div class="col-6 col-md-4 col-lg-4">
              <div class="font-weight-bold"> {{ $t('purchase.payment.total') }} </div>
              <div :class="$style.price"> {{ order.gross_total | currency }} </div>
            </div>
            <div class="col-6 col-md-4 col-lg-4">
              <div class="font-weigth-bold"> {{ $t('purchase.payment.status') }} </div>
              <div>
                <a-badge
                  class="py-1"
                  :count="
                    this.isExpired?
                    'Payment expired' :
                    this.isPaymentPaid ?
                    'Order already paid' :
                    'Waiting payment'"
                    :number-style="{ backgroundColor: colorBadge }" />
              </div>
            </div>
          </div>

          <div class="row mb-2">
            <div class="col-6 col-md-4 col-lg-4">
              <div class="font-weight-bold"> {{ $t('purchase.payment.method') }} </div>
              <div :class="$style.paymentMethod" v-if="paymentInfo">
                <img
                  :src="paymentMeta.image"
                  :alt="paymentInfo.payment_method"
                  :class="$style.paymentMethod"
                  v-if="paymentMeta && paymentMeta.image"
                />
                <div class="font-weight-lighten">
                  {{ paymentInfo.payment_method }}
                </div>
                <div class="font-weight-bold">
                  {{ paymentInfo.va_number }}
                  <a-icon
                    type="copy"
                    style="cursor: pointer"
                    v-clipboard:copy="paymentInfo.va_number"
                    v-clipboard:success="onCopy"
                    v-clipboard:error="onError"
                  />
                </div>
              </div>
            </div>

            <div class="col-6 col-md-4 col-lg-4">
              <div class="font-weigth-bold"> {{ $t('purchase.payment.reference') }} </div>
              <div>
                {{ order.order_id }}
                <a-icon
                  type="copy"
                  style="cursor: pointer"
                  v-clipboard:copy="order.order_id"
                  v-clipboard:success="onCopy"
                  v-clipboard:error="onError"
                />
              </div>
            </div>
          </div>
        </section>

        <section v-if="!!meta && meta.instructions" class="card mb-5 col-12 col-md-10" style="padding: 2rem">
          <h5 class="mb-2"> {{ $t('purchase.payment.instructions') }} </h5>
          <div class="row">
            <div class="col-12 col-md-12 col-lg-8" v-for="(method, methodIndex) in Object.keys(meta.instructions)" :key="methodIndex">
              <a-collapse>
                <a-collapse-panel :key="methodIndex" :header="method">
                  <div>
                    <ol>
                      <li v-for="(step, index) in meta.instructions[method]" :key="index" v-html="step">
                      </li>
                    </ol>
                  </div>
                </a-collapse-panel>
              </a-collapse>
            </div>
          </div>
        </section>

        <section v-if="shippingInfo" class="card mb-5 col-12 col-md-10" style="padding: 2rem">
          <h5 class="mb-2"> {{ $t('purchase.shipping.information') }} </h5>
          <div class="row mb-2">
            <div class="col-6 col-md-6 col-lg-4">
              <div class="font-weight-bold"> {{ $t('purchase.shipping.name') }} </div>
              <div> {{ shippingInfo.name }} </div>
            </div>
            <div class="col-6 col-md-6 col-lg-4">
              <div class="font-weight-bold"> {{ $t('purchase.shipping.phone') }} </div>
              <div> {{ shippingInfo.phone }} </div>
            </div>
          </div>

          <div class="row mb-2">
            <div class="col-6 col-md-6 col-lg-4">
              <div class="font-weight-bold"> {{ $t('purchase.shipping.address') }} </div>
              <div>
                {{ shippingInfo.address }} <br />
                {{ [shippingInfo.sub_district, shippingInfo.district].join(', ') }}{{' '}}
                <br />
                {{ [
                  shippingInfo.city,
                  shippingInfo.province,
                  shippingInfo.country,
                ].join(', ') }}{{' '}}
                - {{ shippingInfo.postal_code }}
              </div>
            </div>
            <div class="col-6 col-md-4 col-lg-4">
              <div class="font-weight-bold"> {{ $t('purchase.courier') }} </div>
              <div class="mb-2">
                <div v-if="shippingInfo.logistic.name !== 'OWN'"> {{ shippingInfo.logistic.name }} </div>
                <div v-if="shippingInfo.logistic.name === 'OWN'"> {{ shippingInfo.logistic.service.toUpperCase() }} </div>
              </div>

              <div class="font-weight-bold"> {{ $t('purchase.awb') }} </div>
              <div class="mb-2" :class="{
                'text-muted small': !shippingInfo.logistic.tracking_no
              }">
                {{ shippingInfo.logistic.tracking_no || 'No AWB provided yet' }}
              </div>
            </div>
          </div>
        </section>

        <section v-if="order" class="card mb-5 col-12 col-md-10" style="padding: 2rem">
          <h5 class="mb-3"> Order items </h5>
          <div class="row">
            <div class="col-12 col-md-11 col-lg-9">
              <div :class="$style.orderItems" >
                <div :class="$style.orderItems__item" v-for="(item, index) in order.items" :key="index">
                  <div class="mr-2 mr-sm-3" :class="[$style.image]">
                    <img
                      :src="`${item.image_url}?tr=w-125,h-125,cm-pad_resize,bg-FFFFFF`"
                      :alt="item.product_title"
                    />
                  </div>
                  <div :class="$style.orderItems__body">
                    <div :class="$style.orderItems__title">
                      {{ item.product_title }}
                    </div>
                    <div class="text-muted small mb-2">
                      SKU: {{ item.sku }}
                    </div>
                    <div class="row mt-auto">
                      <div class="col-6 flex-grow">
                        <div class="text-muted" style="font-size: 14px" v-if="Number(item.price) !== Number(item.sellingprice)">
                          <del> {{ item.price | currency }} </del>
                        </div>
                        <div>
                          {{ item.qty }} ({{ item.uom }}) {{' '}}
                          <span class="text-muted small">x</span>{{' '}}
                          {{ item.item_sellingprice | currency }}
                        </div>
                      </div>
                      <div class="col-6 flex-grow text-right" :class="$style.orderItems__itemTotal">
                        <div></div>
                        <div> {{ item.item_sellingprice * item.qty - (item.voucher_value || 0) | currency }} </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <dl class="row">
            <dt class="col-9 col-md-9 col-lg-7 font-weight-normal text-right"> Total Items: </dt>
            <dd class="col-3 col-md-5 col-lg-2 font-weight-bold text-right">
              <strong> {{ itemTotal | currency }} </strong>
            </dd>

            <!-- <dt class="col-9 col-md-9 col-lg-7 font-weight-normal text-right"> Shipping cost: </dt>
            <dd class="col-3 col-md-5 col-lg-2 font-weight-bold text-right">
              <strong> {{ shippingInfo.logistic.cost | currency }} </strong>
            </dd> -->

            <!-- <template v-if="!!shippingInfo.logistic.insurance_cost">
              <dt class="col-9 col-md-9 col-lg-7 font-weight-normal text-right"> Insurance cost: </dt>
              <dd class="col-3 col-md-5 col-lg-2 font-weight-bold text-right">
                <strong> {{ shippingInfo.logistic.insurance_cost | currency }} </strong>
              </dd>
            </template> -->

            <template v-if="!!voucherValue">
              <dt class="col-9 col-md-9 col-lg-7 font-weight-normal text-right">Voucher Code: </dt>
              <template v-for="(item, index) in  order.voucher_list">
                <dt class="col-9 col-md-9 col-lg-7 font-weight-normal text-right" :key="item.code"> {{ item.code }}: </dt>
                <dd class="col-3 col-md-5 col-lg-2 font-weight-bold text-right" style="color: red;" :key="index">
                  <strong> - {{ item.value | currency }} </strong>
                </dd>
              </template>
            </template>

            <dt class="col-9 col-md-9 col-lg-7 font-weight-normal text-right"> Total Payment: </dt>
            <dd class="col-3 col-md-5 col-lg-2 font-weight-bold text-right" style="font-size: 1rem">
              <strong> {{ total | currency }} </strong>
            </dd>
          </dl>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { mapState } from 'vuex'
import { snakeCase } from 'lodash'
import StateItem from '@/components/Purchase/Detail/stateItem'
import paymentUtils from '@/utils/payment'
import Countdown from '@/components/Countdown/index'

export default {
  name: 'success.create.purchase',
  data () {
    return {
      title: 'Purchase',
    }
  },
  metaInfo () {
    return {
      title: this.title,
    }
  },
  components: {
    StateItem,
    Countdown,
  },
  computed: {
    ...mapState(['settings', 'purchase']),
    order () {
      // return this.data
      if (!this.purchase.createSuccess) {
        return null
      }
      return this.purchase.createSuccess && this.purchase.createSuccess
    },
    paymentInfo () {
      return this.order.payment_info
    },
    shippingInfo () {
      return this.order.shipping_info
    },
    history () {
      return this.order.order_history
    },
    paid () {
      return this.history && this.history.find(
        (item) => ['new', 'confirmed'].includes(item.order_status)
      )
    },
    paymentExpire () {
      return this.paymentInfo && this.$moment(this.paymentInfo.expired_date)
    },
    isPaymentPaid () {
      return !!this.paid
    },
    isExpired () {
      return !this.isPaymentPaid && this.paymentExpire && this.paymentExpire.isBefore(this.$moment())
    },
    created () {
      return this.history && this.history.find(
        (item) => item.order_status == 'waiting_payment'
      )
    },
    colorBadge () {
      return this.isExpired ? 'red' : this.isPaymentPaid ? 'green' : '#FEEB75'
    },
    paymentMeta () {
      return this.paymentInfo?.payment_method && paymentUtils.get(snakeCase(this.paymentInfo.payment_method.toLowerCase()))
    },
    meta () {
      return this.paymentMeta?.qris || !this.paymentInfo?.instruction ? this.paymentMeta : this.paymentInfo.instruction
    },
    voucherValue () {
      return this.order && this.order.discount ? this.order.voucher_list.reduce((acc, value) => acc + value.value, 0) : 0
    },
    voucherProduct () {
      return this.order?.items && this.order.items.reduce((acc, value) => acc + value.voucher_value, 0)
    },
    itemTotal () {
      return this.order?.items && this.order.items.reduce((acc, value) => acc + value.qty * value.item_sellingprice, 0) - (this.voucherProduct ? this.voucherProduct : 0);
    },
    total () {
      return this.shippingInfo?.logistic && this.itemTotal + this.shippingInfo.logistic.cost + this.shippingInfo.logistic.insurance_cost - this.voucherValue
    },
    expiredDate () {
      if (this.paymentInfo) {
        return !!Number(this.paymentInfo.expired_date) ? Number(this.paymentInfo.expired_date * 1000) : this.paymentInfo.expired_date
      } else {
        return null
      }
    },
    shippingState () {
      return this.history && this.history.find((item) => item.order_status === "sent")
    },
    deliveredState () {
      return this.history && this.history.find((item) => item.order_status == "delivered")
    },
  },
  methods: {
    onCopy: function (e) {
      this.$notification.success({
        message: 'copy to clipboard: ' + e.text + ' success',
      })
    },
    onError: function (e) {
      this.$notification.error({
        message: 'Failed to copy the text to the clipboard',
      })
    },
  },
  watch: {
    order: {
      deep: true,
      handler: function (value) {
        // console.log(value, 'disini watch')
        if (value) {
          this.title = `Purchase - ${value.order_id}`
        }
      },
    },
  },
}
</script>

<style lang="scss" module>
@import '@/components/Purchase/Detail/style.module.scss';
</style>
